/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { usePageDesignerMode } from '@salesforce/storefront-next-runtime/design/react/core';
import { Component } from '@/lib/decorators/component';
import { AttributeDefinition } from '@/lib/decorators/attribute-definition';
import { RegionDefinition } from '@/lib/decorators';
import { Link } from '@/components/link';
import { ResponsiveBannerImage } from '@/components/responsive-banner-image';
import { SectionHeader } from '@/components/section-header';
import { cn } from '@/lib/utils';
import type { Image } from '@/types';

/** Full width on mobile (stacked), 55% of the row from md up (757 / 1376 in Figma). */
const SPLIT_HERO_IMAGE_WIDTHS = ['100vw', '100vw', '55vw'];

const splitHeroDefaults = {
    hideOnDesktop: false,
} as const;

@Component('splitHero', {
    name: 'Split Hero',
    group: 'priceshoes_content',
    description:
        'Section with a header (title, subtitle and "Ver más" link) and a split card: image on the left, title, text and CTA button on the right. Desktop only.',
})
@RegionDefinition([])
// oxlint-disable-next-line react/only-export-components -- oxlint flags the co-exported Page Designer metadata class; eslint-plugin-react-refresh does not
export class SplitHeroMetadata {
    @AttributeDefinition({ name: 'Header Title', type: 'string', required: true, description: 'e.g. "Afíliate"' })
    headerTitle!: string;

    @AttributeDefinition({
        name: 'Header Subtitle',
        type: 'string',
        required: true,
        description: 'Short text next to the header title',
    })
    headerSubtitle!: string;

    @AttributeDefinition({ name: 'Header Link Text', type: 'string', required: true, description: 'e.g. "Ver más"' })
    headerLinkText!: string;

    @AttributeDefinition({ name: 'Header Link', type: 'url', required: true })
    headerLinkUrl!: string;

    @AttributeDefinition({ name: 'Image', type: 'image', required: true, description: 'Left side image' })
    image!: string;

    @AttributeDefinition({ name: 'Image Alt Text', type: 'string', required: false })
    imageAlt?: string;

    @AttributeDefinition({
        name: 'Image Link',
        type: 'url',
        required: false,
        description: 'Makes the image clickable. Leave empty for a non-clickable image.',
    })
    imageLink?: string;

    @AttributeDefinition({ name: 'Title', type: 'string', required: true })
    title!: string;

    @AttributeDefinition({ name: 'Text', type: 'string', required: true })
    text!: string;

    @AttributeDefinition({ name: 'Button Text', type: 'string', required: true, description: 'e.g. "Ver más"' })
    ctaText!: string;

    @AttributeDefinition({ name: 'Button Link', type: 'url', required: true })
    ctaLink!: string;

    @AttributeDefinition({
        id: 'hideOnDesktop',
        name: 'Hide on Desktop',
        description: 'Hide the component on desktop (still visible in Page Designer)',
        type: 'boolean',
        defaultValue: splitHeroDefaults.hideOnDesktop,
    })
    hideOnDesktop?: boolean;
}

interface SplitHeroProps {
    headerTitle?: string;
    headerSubtitle?: string;
    headerLinkText?: string;
    headerLinkUrl?: string;
    image?: Image;
    imageAlt?: string;
    imageLink?: string;
    title?: string;
    text?: string;
    ctaText?: string;
    ctaLink?: string;
    hideOnDesktop?: boolean;
    className?: string;
}

/**
 * Split hero: section header + card with the image on the left and the content on the
 * right (55/45). Desktop only on the storefront; the stacked mobile layout is only seen in the
 * Page Designer mobile preview. Only the image (when Image Link is set)
 * and the CTA button navigate.
 */
export default function SplitHero({
    headerTitle,
    headerSubtitle,
    headerLinkText,
    headerLinkUrl,
    image,
    imageAlt,
    imageLink,
    title,
    text,
    ctaText,
    ctaLink,
    hideOnDesktop = splitHeroDefaults.hideOnDesktop,
    className,
}: SplitHeroProps): ReactElement | null {
    const { t } = useTranslation('common');
    const { isDesignMode } = usePageDesignerMode();
    const hasImage = Boolean(image?.url);
    const showCta = Boolean(ctaText?.trim() && ctaLink);
    const isEmpty = !hasImage && !title?.trim() && !text?.trim() && !showCta && !headerTitle?.trim();

    // Desktop only: never shown on mobile. With Hide on Desktop it is not shown at all.
    if (!isDesignMode && (isEmpty || hideOnDesktop)) return null;

    // In design mode it stays visible on every breakpoint so it can always be edited.
    const visibilityClass = !isDesignMode && 'hidden md:block';

    const imageContent = hasImage ? (
        <ResponsiveBannerImage
            image={image}
            alt={imageAlt}
            widths={SPLIT_HERO_IMAGE_WIDTHS}
            className="absolute inset-0 size-full"
        />
    ) : null;

    return (
        <section
            data-slot="split-hero"
            className={cn('w-full px-4 py-6 md:p-8', visibilityClass, className)}
            aria-label={headerTitle?.trim() || title?.trim() || undefined}>
            <SectionHeader
                title={headerTitle}
                subtitle={headerSubtitle}
                linkText={headerLinkText}
                linkUrl={headerLinkUrl}
                className="mb-4 md:mb-6"
            />

            <div className="flex flex-col md:h-[260px] md:flex-row">
                {/* Image: top on mobile, left 55% on desktop (CA2, CA6). */}
                <div className="relative aspect-[16/9] w-full shrink-0 overflow-hidden rounded-t-3xl bg-ps-gray-200 md:aspect-auto md:h-full md:w-[55%] md:rounded-l-3xl md:rounded-tr-none">
                    {imageLink && imageContent ? (
                        <Link
                            to={imageLink}
                            aria-label={imageAlt || title || undefined}
                            className="absolute inset-0 focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-ps-focus">
                            {imageContent}
                        </Link>
                    ) : (
                        imageContent
                    )}
                </div>

                {/* Content: below on mobile, right 45% on desktop (CA3, CA4). */}
                <div className="flex flex-1 flex-col justify-center gap-4 rounded-b-3xl border border-t-0 border-ps-border px-6 py-8 md:rounded-r-3xl md:rounded-bl-none md:border-t md:border-l-0 md:px-10 md:py-14">
                    <div className="flex flex-col gap-2 text-foreground">
                        {title?.trim() ? (
                            <h3 className="text-2xl font-bold leading-9 md:text-[32px] md:leading-[48px]">{title}</h3>
                        ) : (
                            isEmpty && (
                                <h3 className="text-2xl font-bold leading-9 md:text-[32px] md:leading-[48px]">
                                    {t('splitHero.emptyTitle')}
                                </h3>
                            )
                        )}
                        {text?.trim() && <p className="text-base leading-6">{text}</p>}
                    </div>
                    {showCta && (
                        <Link
                            to={ctaLink as string}
                            className="inline-flex w-fit items-center justify-center rounded-lg bg-ps-primary-static px-[22px] py-[11px] text-base font-bold leading-6 text-ps-white hover:bg-ps-pressed-primary focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ps-focus">
                            {ctaText}
                        </Link>
                    )}
                </div>
            </div>
        </section>
    );
}
